import type { Editor } from '@tiptap/react';
import { Fragment, type Node as PMNode } from '@tiptap/pm/model';
import { assertDocumentSectionWrite, sameDocumentSectionValue, splitDocumentSections } from '@cdf/api-zod';
import type { ManagedDocumentContent } from '@cdf/api-client-react';
import { DOCUMENT_AGENT_LIMITS, type DocumentAgentMode } from '@cdf/file-core/browser';
import { showAgentChangeFlash, type AgentChangeRange } from './agent-change-flash';

type SectionDocument = Parameters<typeof splitDocumentSections>[0];
type JsonNode = { type?: string; text?: string; content?: JsonNode[] };
const asSections = (value: ManagedDocumentContent) => value as unknown as SectionDocument;

/** The whole-document review group used when a document has no defined sections. */
export const WHOLE_DOCUMENT = 'document';

export interface AgentEditGroup {
  id: string;
  title: string;
  before: string;
  after: string;
  /** Can be applied through combined review. Bulk-protected sections apply only on their own. */
  eligible: boolean;
  separate: boolean;
  reason?: string;
}

function textOf(nodes: readonly JsonNode[] | undefined): string {
  return (nodes ?? []).map(node => node.text ?? textOf(node.content)).join('').trim();
}
function blocksText(nodes: readonly unknown[]): string {
  return (nodes as JsonNode[]).map(node => node.text ?? textOf(node.content)).filter(Boolean).join('\n');
}

/** Groups a proposal's differences by section so a person can review and choose them. */
export function agentEditGroups(current: ManagedDocumentContent, proposed: ManagedDocumentContent): AgentEditGroup[] {
  const sections = current.sections;
  if (sections && proposed.sections && sameDocumentSectionValue(sections, proposed.sections)) {
    const before = splitDocumentSections(asSections(current)), after = splitDocumentSections(asSections(proposed));
    return sections.items.flatMap(section => {
      const old = before.parts.get(section.id), next = after.parts.get(section.id);
      if (!old || !next || sameDocumentSectionValue(old, next)) return [];
      const headingChanged = !sameDocumentSectionValue(old.prefix, next.prefix);
      const reason = section.hidden ? 'Hidden' : section.protection === 'locked' ? 'Locked' : headingChanged ? 'The section heading changed' : section.protection === 'bulk_protected' ? 'Edit separately' : undefined;
      return [{ id: section.id, title: section.title, before: blocksText(old.content), after: blocksText(next.content),
        eligible: !section.hidden && section.protection !== 'locked' && !headingChanged, separate: section.protection === 'bulk_protected', reason }];
    });
  }
  if (sameDocumentSectionValue(current.content, proposed.content)) return [];
  return [{ id: WHOLE_DOCUMENT, title: 'Whole document', before: blocksText(current.content), after: blocksText(proposed.content),
    eligible: !sections, separate: false, reason: sections ? 'Sections changed; ask for a section edit' : undefined }];
}

/** Explains why a chosen set of groups cannot be confirmed together, or returns null. */
export function agentSelectionProblem(groups: readonly AgentEditGroup[], ids: readonly string[]): string | null {
  if (!ids.length) return 'Choose at least one change to review.';
  if (ids.length > DOCUMENT_AGENT_LIMITS.targetSections) return 'Choose ' + DOCUMENT_AGENT_LIMITS.targetSections + ' sections or fewer.';
  const chosen = ids.map(id => groups.find(group => group.id === id));
  if (chosen.some(group => !group?.eligible)) return 'Locked, hidden and changed-heading sections must be edited manually.';
  if (chosen.length > 1 && chosen.some(group => group?.separate)) return 'Protected sections require the single-section control.';
  return null;
}

/** Client mirror of the server's section adoption: only chosen sections change; protection is re-checked. */
export function selectAgentEdits(current: ManagedDocumentContent, proposed: ManagedDocumentContent, ids: readonly string[]): ManagedDocumentContent {
  if (ids.length === 1 && ids[0] === WHOLE_DOCUMENT) {
    assertDocumentSectionWrite(asSections(current), asSections(proposed));
    return { ...current, content: proposed.content };
  }
  if (!current.sections || !sameDocumentSectionValue(current.sections, proposed.sections)) throw new Error('Select defined sections from this proposal.');
  const before = splitDocumentSections(asSections(current)), after = splitDocumentSections(asSections(proposed));
  for (const id of ids) {
    const part = after.parts.get(id);
    if (!part) throw new Error('Select defined sections from this proposal.');
    before.parts.set(id, part);
  }
  const result = { ...current, content: [...before.preamble, ...current.sections.items.flatMap(section => {
    const part = before.parts.get(section.id);
    return part ? [...part.prefix, ...part.content] : [];
  })] } as ManagedDocumentContent;
  assertDocumentSectionWrite(asSections(current), asSections(result), ids.length === 1 ? ids[0] : undefined);
  return result;
}

const trackedName = (name: string) => name === 'trackedInsertion' || name === 'trackedDeletion';
function hasTrackedMarks(nodes: readonly PMNode[]) {
  let found = false;
  for (const node of nodes) node.descendants(child => { if (child.marks.some(mark => trackedName(mark.type.name))) found = true; return !found; });
  return found;
}
function plainTextBlock(node: PMNode) {
  let plain = node.isTextblock;
  node.forEach(child => { if (!child.isText) plain = false; });
  return plain;
}
const offsetOf = (doc: PMNode, index: number) => { let position = 0; for (let i = 0; i < index; i++) position += doc.child(i).nodeSize; return position; };

/** What Cancel needs to restore an Auto-update change while its notice is shown. */
export interface AgentEditUndo { from: number; before: unknown[]; after: unknown[] }
export type AgentEditResult =
  | { ok: true; changes: number; tracked: boolean; changeId: string; undo?: AgentEditUndo }
  | { ok: false; message: string };

/**
 * Applies the reviewed content as a collaborator through the editor, so the live session carries it.
 * Co-author pairs changed paragraphs and records tracked insertions/deletions; other structure changes and
 * Auto-update replace the changed blocks directly (saved-group review still records them when tracking is on).
 */
export interface AgentEditOptions {
  changeId?: string;
  /** An already-uploaded image the person chose in review; inserted after the changed text. */
  image?: { src: string; alt: string; title?: string };
  /** Check that the change can be applied without changing the document. */
  dryRun?: boolean;
}
export function applyAgentEdit(editor: Editor, next: ManagedDocumentContent, mode: DocumentAgentMode, options: AgentEditOptions = {}): AgentEditResult {
  const changeId = options.changeId ?? crypto.randomUUID();
  if (editor.isDestroyed || !editor.isEditable) return { ok: false, message: 'Editing is unavailable. Reconnect to the document and try again.' };
  const doc = editor.state.doc;
  let target: PMNode;
  try { target = editor.schema.nodeFromJSON({ type: 'doc', content: next.content }); }
  catch { return { ok: false, message: 'The proposed content cannot be shown in this editor.' }; }
  let start = 0;
  while (start < doc.childCount && start < target.childCount && doc.child(start).eq(target.child(start))) start++;
  let endOld = doc.childCount, endNew = target.childCount;
  while (endOld > start && endNew > start && doc.child(endOld - 1).eq(target.child(endNew - 1))) { endOld--; endNew--; }
  if (start === endOld && start === endNew) return { ok: false, message: 'The document already contains this change.' };
  const oldNodes: PMNode[] = [], newNodes: PMNode[] = [];
  for (let i = start; i < endOld; i++) oldNodes.push(doc.child(i));
  for (let i = start; i < endNew; i++) newNodes.push(target.child(i));
  if (hasTrackedMarks(oldNodes)) return { ok: false, message: 'Accept or reject the pending inline edit before the agent changes this text.' };
  if (options.image) {
    try { newNodes.push(editor.schema.nodeFromJSON({ type: 'image', attrs: { src: options.image.src, alt: options.image.alt, title: options.image.title ?? null } })); }
    catch { return { ok: false, message: 'The chosen image cannot be inserted here.' }; }
  }
  if (options.dryRun) return { ok: true, changes: Math.max(oldNodes.length, newNodes.length), tracked: false, changeId };
  const from = offsetOf(doc, start), to = offsetOf(doc, endOld);
  const tr = editor.state.tr;
  const pairable = !options.image && mode === 'coauthor' && oldNodes.length === newNodes.length && oldNodes.every((node, index) => {
    const other = newNodes[index]!;
    return plainTextBlock(node) && plainTextBlock(other) && node.type === other.type && sameDocumentSectionValue(node.attrs, other.attrs);
  });
  let ranges: AgentChangeRange[] = [], changes = 0;
  if (pairable) {
    const insertion = editor.schema.marks.trackedInsertion!, deletion = editor.schema.marks.trackedDeletion!;
    let position = from;
    const edits: Array<{ at: number; delFrom: number; delTo: number; insert: Fragment }> = [];
    oldNodes.forEach((node, index) => {
      const other = newNodes[index]!, a = node.textContent, b = other.textContent;
      if (!node.eq(other)) {
        let prefix = 0;
        while (prefix < a.length && prefix < b.length && a[prefix] === b[prefix]) prefix++;
        let suffix = 0;
        while (suffix < a.length - prefix && suffix < b.length - prefix && a[a.length - 1 - suffix] === b[b.length - 1 - suffix]) suffix++;
        const inserted: PMNode[] = [];
        other.slice(prefix, b.length - suffix).content.forEach(child => inserted.push(child.mark(insertion.create({ changeId }).addToSet(child.marks))));
        edits.push({ at: position, delFrom: position + 1 + prefix, delTo: position + 1 + a.length - suffix, insert: Fragment.fromArray(inserted) });
      }
      position += node.nodeSize;
    });
    // Later blocks first, so earlier positions stay valid.
    for (const edit of edits.reverse()) {
      if (edit.delTo > edit.delFrom) tr.addMark(edit.delFrom, edit.delTo, deletion.create({ changeId }));
      if (edit.insert.size) tr.insert(edit.delTo, edit.insert);
    }
    changes = edits.length;
    tr.doc.descendants((node, pos) => {
      if (node.isText && node.marks.some(mark => trackedName(mark.type.name) && mark.attrs.changeId === changeId)) ranges.push({ from: pos, to: pos + node.nodeSize });
    });
  } else {
    const content = Fragment.fromArray(newNodes);
    tr.replaceWith(from, to, content);
    ranges = [{ from, to: from + content.size }];
    changes = Math.max(oldNodes.length, newNodes.length);
  }
  tr.setMeta('documentAgentEdit', true).setMeta('addToHistory', true);
  editor.view.dispatch(tr);
  showAgentChangeFlash(editor.view, changeId, ranges);
  return { ok: true, changes, tracked: pairable, changeId,
    ...(pairable ? {} : { undo: { from, before: oldNodes.map(node => node.toJSON()), after: newNodes.map(node => node.toJSON()) } }) };
}

/** Cancel for Auto-update: restores the original blocks only if nobody changed the agent's text since. */
export function cancelAgentEdit(editor: Editor, undo: AgentEditUndo): { ok: true } | { ok: false; message: string } {
  if (editor.isDestroyed || !editor.isEditable) return { ok: false, message: 'Editing is unavailable, so the change could not be cancelled.' };
  try {
    const after = Fragment.fromArray(undo.after.map(json => editor.schema.nodeFromJSON(json)));
    const before = Fragment.fromArray(undo.before.map(json => editor.schema.nodeFromJSON(json)));
    const to = undo.from + after.size;
    if (to > editor.state.doc.content.size || !editor.state.doc.slice(undo.from, to).content.eq(after))
      return { ok: false, message: 'The changed text was edited since, so it was kept. Undo it manually if needed.' };
    editor.view.dispatch(editor.state.tr.replaceWith(undo.from, to, before).setMeta('documentAgentEdit', true).setMeta('addToHistory', true));
    return { ok: true };
  } catch {
    return { ok: false, message: 'The change could not be cancelled. Undo it manually if needed.' };
  }
}
